import type { TFunction } from 'i18next';
import {
  TutorBookingChangeProposal,
  TutorBookingChangeStatus,
} from '../models/TutorBookingChange';
import { parseBookingDisplayInstant } from './bookingInstant';
import { isAtTeacherSession } from './tutorSessionType';

/**
 * Tutor booking moderation — align with backend enums that may differ slightly
 * (e.g. AWAITING_CONFIRMATION vs PENDING_CONFIRMATION).
 */

const MODERATABLE_STATUSES = new Set([
  'AWAITING_CONFIRMATION',
  'PENDING_CONFIRMATION',
  'PENDING',
  'SUBMITTED',
  'NEW',
]);

export function normalizeTutorBookingStatus(status: string | undefined | null): string {
  if (status == null || status === '') return '';
  return String(status)
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_');
}

/** True when tutor/host can call accept/decline APIs for this booking. */
export function isTutorBookingModeratable(status: string | undefined | null): boolean {
  return MODERATABLE_STATUSES.has(normalizeTutorBookingStatus(status));
}

/** Booking start is in the past (same idea as room booking requests). */
export function isPastTutorBookingStart(startTime: string | undefined | null): boolean {
  if (!startTime) return false;
  const bookingStart = parseBookingDisplayInstant(startTime);
  if (Number.isNaN(bookingStart.getTime())) return false;
  return bookingStart.getTime() < Date.now();
}

export function tutorBookingAcceptDisabled(status: string | undefined | null, startTime: string): boolean {
  return isPastTutorBookingStart(startTime) || !isTutorBookingModeratable(status);
}

export function tutorBookingDeclineDisabled(status: string | undefined | null, startTime: string): boolean {
  return isPastTutorBookingStart(startTime) || !isTutorBookingModeratable(status);
}

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

export function getTutorBookingStatusChipColor(status: string | undefined | null): ChipColor {
  const s = normalizeTutorBookingStatus(status);
  if (s === 'CONFIRMED') return 'success';
  if (s === 'CANCELLED' || s === 'DECLINED' || s === 'REJECTED') return 'error';
  if (isTutorBookingModeratable(status)) return 'warning';
  return 'default';
}

export function getTutorBookingStatusChipVariant(status: string | undefined | null): 'outlined' | 'filled' {
  const s = normalizeTutorBookingStatus(status);
  if (s === 'CANCELLED' || s === 'DECLINED' || s === 'REJECTED') return 'outlined';
  // Pending / confirmed use filled so status reads clearly on list cards.
  return 'filled';
}

/** Map API tutor-booking statuses to existing `status.*` copy (room booking parity). */
const TUTOR_BOOKING_STATUS_I18N: Record<string, string> = {
  AWAITING_CONFIRMATION: 'status.awaiting_confirmation',
  PENDING_CONFIRMATION: 'status.pending_confirmation',
  PENDING: 'status.pending',
  SUBMITTED: 'status.pending',
  NEW: 'status.pending',
  CONFIRMED: 'status.confirmed',
  CANCELLED: 'status.cancelled',
  DECLINED: 'status.declined',
  REJECTED: 'status.declined',
};

export function tutorBookingStatusLabel(t: TFunction, status: string | undefined | null): string {
  const n = normalizeTutorBookingStatus(status);
  if (!n) return '—';
  const path = TUTOR_BOOKING_STATUS_I18N[n];
  if (path) return t(path);
  return t(`status.${n.toLowerCase()}`, { defaultValue: status || '—' });
}

/** User-facing: booking is no longer active (host/tutor declined or user/org cancelled). */
export function isUserTutorBookingCancelledStatus(status: string | undefined | null): boolean {
  const s = normalizeTutorBookingStatus(status);
  return s === 'CANCELLED' || s === 'DECLINED' || s === 'REJECTED';
}

/** Host may propose a new lesson time when booking is pending or confirmed and not in the past. */
export function canHostProposeTutorBookingChange(
  status: string | undefined | null,
  startTime: string,
  changeProposal?: TutorBookingChangeProposal | null,
): boolean {
  if (isPastTutorBookingStart(startTime)) return false;
  const s = normalizeTutorBookingStatus(status);
  if (s === 'CANCELLED' || s === 'DECLINED' || s === 'REJECTED') return false;
  if (s !== 'CONFIRMED' && !isTutorBookingModeratable(status)) return false;
  if (!changeProposal) return true;
  return (
    changeProposal.status === TutorBookingChangeStatus.DECLINED ||
    changeProposal.status === TutorBookingChangeStatus.ACCEPTED ||
    changeProposal.status === TutorBookingChangeStatus.CANCELLED
  );
}

/**
 * Host/tutor may cancel an already-confirmed future lesson (same decline API as
 * rejecting a request; mirrors host room “cancel approved booking”).
 */
export function canCancelConfirmedTutorBooking(
  status: string | undefined | null,
  startTime: string,
): boolean {
  if (isPastTutorBookingStart(startTime)) return false;
  return normalizeTutorBookingStatus(status) === 'CONFIRMED';
}

export function canHostCancelTutorBookingChangeProposal(
  changeProposal?: TutorBookingChangeProposal | null,
): boolean {
  return changeProposal?.status === TutorBookingChangeStatus.PENDING_REPLY;
}

/**
 * Host may assign/reassign a room when the lesson is offline, confirmed, has a space,
 * and has not started yet (matches backend validateTutorBookingRoomAssignable).
 */
export function canHostAssignTutorBookingRoom(params: {
  status: string | undefined | null;
  startTime: string;
  spaceId: string | null | undefined;
  lessonSessionType: string | null | undefined;
}): boolean {
  if (!isAtTeacherSession(params.lessonSessionType)) return false;
  if (!params.spaceId) return false;
  if (isPastTutorBookingStart(params.startTime)) return false;
  return normalizeTutorBookingStatus(params.status) === 'CONFIRMED';
}

/** Show room name / empty state for at-teacher lessons that belong to a space. */
export function canShowTutorBookingRoomSection(params: {
  spaceId: string | null | undefined;
  lessonSessionType: string | null | undefined;
}): boolean {
  return isAtTeacherSession(params.lessonSessionType) && !!params.spaceId;
}

/** True when the student has confirmed attendance (`userConfirmedAt` set). */
export function hasUserConfirmedTutorLesson(
  userConfirmedAt: string | null | undefined,
): boolean {
  return userConfirmedAt != null && String(userConfirmedAt).trim() !== '';
}

/**
 * User may confirm attendance when the org gate is on, the booking is CONFIRMED,
 * and attendance is not yet confirmed. Do not infer from reminder delivery.
 */
export function canUserConfirmTutorLessonAttendance(params: {
  status: string | undefined | null;
  userConfirmedAt: string | null | undefined;
  /** Org-derived gate; missing/false hides the CTA. */
  confirmAttendanceEnabled?: boolean | null;
}): boolean {
  return (
    params.confirmAttendanceEnabled === true &&
    normalizeTutorBookingStatus(params.status) === 'CONFIRMED' &&
    !hasUserConfirmedTutorLesson(params.userConfirmedAt)
  );
}

/** Whether to show attendance ticks / CTA chrome for a booking. */
export function shouldShowTutorLessonAttendanceUi(params: {
  status: string | undefined | null;
  userConfirmedAt: string | null | undefined;
  confirmAttendanceEnabled?: boolean | null;
}): boolean {
  if (normalizeTutorBookingStatus(params.status) !== 'CONFIRMED') {
    return false;
  }
  return (
    params.confirmAttendanceEnabled === true ||
    hasUserConfirmedTutorLesson(params.userConfirmedAt)
  );
}
